import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import { Mail, User, Send, Flame } from "lucide-react";

export default function Messages() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const endRef = useRef(null);

  const { data: threads = [] } = useQuery({ queryKey: ["threads"], queryFn: async () => (await api.get("/messages/threads")).data, enabled: !!user, refetchInterval: 15000 });
  const { data: thread } = useQuery({ queryKey: ["thread", userId], queryFn: async () => (await api.get(`/messages/thread/${userId}`)).data, enabled: !!user && !!userId, refetchInterval: 8000 });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);
  useEffect(() => { if (userId) qc.invalidateQueries({ queryKey: ["threads"] }); }, [thread, userId, qc]);

  const send = async () => {
    if (!body.trim() || !userId) return;
    try {
      await api.post("/messages", { to_user_id: userId, body });
      setBody("");
      qc.invalidateQueries({ queryKey: ["thread", userId] });
      qc.invalidateQueries({ queryKey: ["threads"] });
      qc.invalidateQueries({ queryKey: ["unread"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const acceptRequest = async () => {
    try {
      await api.post(`/messages/thread/${userId}/accept`);
      qc.invalidateQueries({ queryKey: ["thread", userId] });
      qc.invalidateQueries({ queryKey: ["threads"] });
      toast.success(t("msg_request_accepted") || "Demande acceptée");
    } catch { toast.error("Error"); }
  };

  const declineRequest = async () => {
    try {
      await api.post(`/messages/thread/${userId}/decline`);
      qc.invalidateQueries({ queryKey: ["threads"] });
      navigate("/messages");
      toast.success(t("msg_request_declined") || "Demande refusée");
    } catch { toast.error("Error"); }
  };

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/messages")}`;
  };

  if (loading) return <div className="py-24 text-center text-stone-400">{t("loading")}</div>;
  if (!user) return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      <Flame className="w-12 h-12 text-amber-500 mx-auto mb-6" />
      <h1 className="font-serif text-2xl text-amber-100 mb-4">{t("inbox_title")}</h1>
      <button data-testid="inbox-login-btn" onClick={handleLogin} className="bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-6 py-3 rounded-md">{t("login_google")}</button>
    </div>
  );

  return (
    <>
      <Seo title={t("inbox_title")} canonicalPath="/messages" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <h1 className="font-serif text-3xl sm:text-4xl uppercase tracking-tight text-amber-100 mb-8 flex items-center gap-3"><Mail className="w-7 h-7 text-amber-500" /> {t("inbox_title")}</h1>

        <div className="grid md:grid-cols-[300px_1fr] gap-6 bg-[#0B0C0E] border border-[#272A34] rounded-xl overflow-hidden" style={{ minHeight: "60vh" }}>
          {/* Threads */}
          <aside className="border-r border-[#272A34] overflow-y-auto">
            {threads.length === 0 ? (
              <p className="p-6 text-stone-400 text-sm" data-testid="inbox-empty">{t("inbox_empty")}</p>
            ) : threads.map((th) => (
              <button key={th.user.user_id} data-testid={`thread-${th.user.user_id}`} onClick={() => navigate(`/messages/${th.user.user_id}`)}
                className={`w-full text-left flex items-center gap-3 p-4 border-b border-[#272A34] transition-colors ${userId === th.user.user_id ? "bg-amber-900/15" : "hover:bg-[#181A20]"}`}>
                {th.user.picture ? <img src={th.user.picture} alt="" className="w-10 h-10 rounded-full object-cover" /> : <span className="w-10 h-10 rounded-full bg-amber-900/40 flex items-center justify-center"><User className="w-5 h-5 text-amber-400" /></span>}
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-stone-100 truncate">{th.user.name}</div>
                  <div className="text-xs text-stone-500 truncate">{th.last_message}</div>
                </div>
                {th.is_request && <span data-testid="thread-request-badge" className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-600/40 text-amber-300">demande</span>}
                {th.unread > 0 && <span className="w-5 h-5 rounded-full bg-amber-500 text-stone-950 text-[10px] font-bold flex items-center justify-center">{th.unread}</span>}
              </button>
            ))}
          </aside>

          {/* Thread */}
          <section className="flex flex-col">
            {!userId ? (
              <div className="flex-1 flex items-center justify-center text-stone-500">{t("msg_select")}</div>
            ) : (
              <>
                <div className="flex items-center gap-3 p-4 border-b border-[#272A34]">
                  {thread?.other?.picture ? <img src={thread.other.picture} alt="" className="w-9 h-9 rounded-full object-cover" /> : <span className="w-9 h-9 rounded-full bg-amber-900/40 flex items-center justify-center"><User className="w-4 h-4 text-amber-400" /></span>}
                  <span className="font-serif text-stone-100">{thread?.other?.name || "…"}</span>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="message-list">
                  {(thread?.messages || []).map((m) => {
                    const mine = m.from_user_id === user.user_id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${mine ? "bg-amber-600 text-stone-950 rounded-br-sm" : "bg-[#181A20] text-stone-200 border border-[#272A34] rounded-bl-sm"}`}>
                          <p className="text-sm whitespace-pre-line break-words">{m.body}</p>
                          <div className={`text-[10px] mt-1 font-mono ${mine ? "text-stone-800" : "text-stone-500"}`}>{formatDate(m.created_at, lang)}</div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
                {thread?.is_request ? (
                  <div className="p-4 border-t border-[#272A34]" data-testid="message-request-actions">
                    <p className="text-sm text-stone-400 mb-3">{t("msg_request_prompt") || `${thread?.other?.name || "Ce membre"} vous a envoyé une demande de message.`}</p>
                    <div className="flex gap-2">
                      <button data-testid="accept-request-btn" onClick={acceptRequest} className="flex-1 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold py-2 rounded-md">{t("msg_accept") || "Accepter"}</button>
                      <button data-testid="decline-request-btn" onClick={declineRequest} className="flex-1 bg-[#181A20] hover:bg-[#20232b] border border-[#272A34] text-stone-300 py-2 rounded-md">{t("msg_decline") || "Refuser"}</button>
                    </div>
                  </div>
                ) : thread?.i_requested ? (
                  <div className="p-4 border-t border-[#272A34] text-center text-sm text-stone-500" data-testid="request-pending">
                    {t("msg_request_pending") || "En attente de l'acceptation de votre demande…"}
                  </div>
                ) : (
                  <div className="p-4 border-t border-[#272A34] flex gap-2">
                    <input data-testid="message-input" value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("msg_placeholder")} className="flex-1 bg-[#181A20] border border-[#272A34] rounded-md px-3 py-2.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" />
                    <button data-testid="message-send-btn" onClick={send} disabled={!body.trim()} className="bg-amber-600 hover:bg-amber-500 text-stone-950 px-4 rounded-md disabled:opacity-50"><Send className="w-4 h-4" /></button>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
